import React, { useState, useEffect, } from 'react'
import { Row, Col, Alert, } from 'reactstrap'
import { Link, } from 'react-router-dom'
import ProgramTag from '../Search/ProgramSearch/ProgramTag'
import DetailHeader from './DetailHeader'
import DetailLocationSection from './DetailLocationSection'
import ErrorBoundary from '../ErrorBoundary'
import HowToApplySection from './HowToApplySection'
import ResourcesSection from './ResourcesSection'
import { getAbortController, } from '../../api/ApiBase'
import { getDetail, } from '../../api/DirectoryApi'
import { arrayIsNullOrEmpty, } from '../../util/helpers'
import { ProgramSearchResult } from '../../types/api'

type ProgramDetailProps = {
  programOccupationId: number | string, 
  program            : ProgramSearchResult | null, 
  setActiveProgram   : Function, 
  mapLibReady        : boolean,
  programType        : string | null,
  pathname           : string,
  zipcode            : string,
  userLocation       : object,
}

const ProgramDetail : React.FC<ProgramDetailProps> = ({ 
  programOccupationId: searchId = null, 
  program, 
  userLocation = null,
  setActiveProgram, 
  mapLibReady = false,
  programType: searchProgramType = null,
  pathname = '',
  zipcode = '',
}) => {
  // SETUP ////////////////////////////////////////////////////////////////////
  // Initialize variables.
  const [ demoWarn, setDemoWarn, ] = useState('')
  const [ showHowToApply, setShowHowToApply, ] = useState(false)
  const programMapCallback = React.useRef(null)
  const howToApplyRef = React.useRef<HTMLElement | null>(null)
  const resourcesRef = React.useRef<HTMLElement | null>(null)
  const abortController = React.useRef<AbortController | null>(null)
  let progType = null
  let progOrOccId = null
  let progWebsite = null

  // If the program exists, populate variables using its data.
  program = checkIfExists(program) ? program : null
  if (program) {
    const storedType = program.type
    const storedProgOrOccId = program.program_occupation_id
    const storedProgWebsite = program.program_url

    progType = checkIfExists(storedType) ? storedType : null
    progOrOccId = checkIfExists(storedProgOrOccId) ? storedProgOrOccId : null
    progWebsite = checkIfExists(storedProgWebsite) ? storedProgWebsite : null 

    // Preface URL's if necessary.
    if (progWebsite && !storedProgWebsite.startsWith('http')) {
      progWebsite = `https://${storedProgWebsite}`
    }
  }

  // If we don't have the program type, but do have the search program type, substitute it in.
  const onlyHaveSearchProgramType = progType == null && checkIfExists(searchProgramType)
  progType = onlyHaveSearchProgramType ? searchProgramType : progType

  // If we don't have an occupation ID, but do have a search ID, substitute it in.
  const onlyHaveSearchId = progOrOccId == null && checkIfExists(searchId)
  progOrOccId = onlyHaveSearchId ? searchId : progOrOccId

  // CALLBACKS ////////////////////////////////////////////////////////////////
  // This is related to the map's behavior.
  // Note: May not be in use anymore?
  const subscribeMapChanges = React.useCallback((callback) => {
    if (checkIfExists(programMapCallback) && programMapCallback.current !== callback) {
      programMapCallback.current = callback
    }
  }, [ programMapCallback, ])

  // "How to Apply" button that triggers a downward scroll.
  // Note: May not be in use anymore?
  const scrollToHowToApply = React.useCallback(() => {
    if (howToApplyRef && howToApplyRef.current && howToApplyRef.current !== null) {
      howToApplyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', })
    }
  }, [ howToApplyRef, ])

  // "Additional Resources" button that triggers a downward scroll. 
  const scrollToResources = React.useCallback(() => {
    if (resourcesRef && resourcesRef.current && resourcesRef.current !== null) {
      resourcesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', })
    }
  }, [ resourcesRef, ])

  // USE EFFECTS //////////////////////////////////////////////////////////////
  // Run this after first render and any subsequent updates.
  useEffect(() => {
    // Set the ID.
    let idToUse = progOrOccId != null ? progOrOccId : -1
    idToUse = typeof idToUse === 'string' ? parseInt(idToUse) : idToUse
    if (!progType || idToUse < 0) {
      setDemoWarn('The specified program ID or type is not valid.')
      return
    }

    // Configure an abort controller.
    const abtctl = abortController
    if (abtctl != null && abtctl !== undefined && abtctl.current != null && abtctl.current !== undefined) {
      if (!abtctl.current.signal.aborted) {
        abtctl.current.abort('Cancelling current request for a new one.')
      }
    }
    abtctl.current = getAbortController()

    // Handle successes.
    const detailGetSuccess = (response: any) => {
      const { data, } = response

      // Handle errors.
      // Note: Why is this code here when we have a failure handler below?
      if (data && data.errors) {
        console.log(`Errors in detail response.`)
        setDemoWarn(JSON.stringify(data.errors))
      }

      // Handle successes.
      else if (data) {
        setActiveProgram(data)

        // Commented out this segment. It's throwing a syntax error and doesn't seem to break anything.
        // if (programMapCallback && programMapCallback.current && data && data.addresses){
        //   programMapCallback.current(data.addresses)
        // }
      }

      // Handle empty response.
      else {
        console.log('No detail response.')
      }

      // Clear the abort control handler.
      abtctl.current = null
    }

    // Handle failures.
    const detailGetFailure = (err: any, isCancel = false) => {
      if (isCancel !== true) {
        if (err.response && err.response.status === 404) {
          setDemoWarn(`Program details were not found. If the error persists, please contact support at ${process.env.REACT_APP_SUPPORT_EMAIL}.`)
          console.error('Program details were not found.', err.response.toString())
        } else {
          setDemoWarn(`An error occurred requesting detailed information for ${progType} program #${idToUse}.`)
          console.error('Error requesting program', err.toString())
        }
      }

      // Clear the abort control handler.
      abtctl.current = null
    }

    // Fetch the detail information.
    getDetail(progType, idToUse, detailGetSuccess, detailGetFailure, abtctl.current.signal)
    return
  }, [ progOrOccId, setActiveProgram, progType , ])

  // Run this after first render and any subsequent updates.
  useEffect(() => {
    const abtctl = abortController
    return () => {
      if (abtctl != null && abtctl !== undefined && abtctl.current != null && abtctl.current !== undefined) {
        abtctl.current.abort('Detail component is unmounting.')
      }
    }
  }, [])

  // DYNAMIC CONTENT //////////////////////////////////////////////////////////
  // Generate contact information.
  const headerTags = []
  if (program) {
    if (program.primary_contact) {
      const { primary_contact, } = program
      if (primary_contact.address && primary_contact.address.address) {
        headerTags.push({
          type: 'address',
          text: primary_contact.address.address,
        })
      }
      if (primary_contact.phone){
        headerTags.push({
          type: 'phone',
          text: primary_contact.phone,
        })
      }
      if (primary_contact.email) {
        headerTags.push({
          type   : 'email', 
          text   : primary_contact.email,
          content: primary_contact.email,
        })
      } 
    }
    if (progWebsite) {
      headerTags.push({
        type   : 'website', 
        text   : 'Visit Program Website',
        content: progWebsite,
      })
    }
  }

  // Generate the "Career XXX" symbol denoting the program's type.
  const programTags = []
  if (program) {
    const { categories, main_title, other_title, } = program
    const isLaunch = (categories && Array.isArray(categories) && categories.includes('launch'))
    const isExplore = (categories && Array.isArray(categories) && categories.includes('explore'))
    const isPrep = (categories && Array.isArray(categories) && categories.includes('prep'))
    if (main_title){
      if (isLaunch) {
        programTags.push(<ProgramTag key={'pgm-tag-main'} type={'launch'} text={main_title} className={'body-text-grey'}/>)
      } else if (isExplore) {
        programTags.push(<ProgramTag key={'pgm-tag-main'} type={'explore'} text={main_title} className={'body-text-grey'}/>)
      } else if (isPrep) {
        programTags.push(<ProgramTag key={'pgm-tag-main'} type={'prep'} text={main_title} className={'body-text-grey'}/>)
      } else {
        programTags.push(<ProgramTag key={'pgm-tag-main'} text={main_title} className={'body-text-grey'}/>)
      }
    }
    if (other_title) {
      programTags.push(<ProgramTag key={'pgm-tag-other'} text={other_title} className={'body-text-grey'}/>)
    }
  }
  
  // OUTPUT ///////////////////////////////////////////////////////////////////
  // If we don't have a program to display, return a blank page.
  if (!program){
    return <Row className={'bg-white'}>
      <Col>
        <Link to={pathname} className={'goback-button link'}>Back to Search</Link>
        <Row>
          <Col>
            {
              (demoWarn && demoWarn.length > 0) 
                ? <Alert color={'wsac-red'}>
                  <b>{demoWarn}</b>
                </Alert>
                : 'Loading Program Information...'
            }
          </Col>
        </Row>
      </Col>
    </Row>
  }

  // Otherwise, return a populated page.
  else {
    return (
      <Row>
        <Col xs={12}>
  
          <Row className={'program-detail-header p-2 p-md-4 pb-5'}>
            <Col>
              <Link to={pathname} className={'goback-button link'}>Back to Search</Link>
              {
                demoWarn && demoWarn.length > 0 && 
                <Alert variant={'danger'}>
                  <b>{demoWarn}</b>
                </Alert>
              }
              <DetailHeader
                occupationName={program.occupation_name}
                programName={program.program_name}
                programTags={programTags}
                baseWage={program.base_hourly_wage}
                programLength={program.program_length}
                startDate={program.program_start_date}
                endDate={program.program_end_date}
                headerTags={headerTags}
                hasApplicationInfo={showHowToApply}
                onHowToApplyClick={scrollToHowToApply}
                onResourcesClick={scrollToResources}
              />
            </Col>
          </Row>
          <Row className={'p-2 px-md-4 my-md-4'}>
            <Col md={8} xs={12}>
              <Row className={'mb-4'}>
                <Col xs={12}>
                  <h3 className={'heading-3 mb-0'}>Program Information</h3>
                </Col>
              </Row>
              <Row className={'mb-4'}>
                { 
                  !arrayIsNullOrEmpty(program.program_levels) &&
                    <Col xs={6}>
                      <h4 className={'heading-4'}>Program Level</h4>
                      {program.program_levels.map((lvl, idx) => {
                        if (lvl) {
                          return <div key={`lvl-${idx}`}><span className={'body-text'}>{lvl}</span></div>
                        } 
                        return null;
                      })}
                    </Col>
                }
                {
                  program.minimum_education_requirement !== null &&
                    <Col xs={6}>
                      <h4 className={'heading-4'}>Minimum Education Requirements</h4>
                      <p className={'body-text'}>{program.minimum_education_requirement}</p>
                    </Col>
                }
                {
                  !arrayIsNullOrEmpty(program.tags) && 
                    <Col xs={6}>
                      <h4 className={'heading-4 mt-2'}>Tags</h4>
                      {
                        program.tags.map((tag, idx) => {
                          if (tag != null) {
                            return <div key={`tag-${idx}`}><span className={'body-text'}>{tag}</span></div>;
                          } 
                          return null
                        })
                      }
                    </Col>
                }
              </Row>
              <Row className={'mb-4'}>
                <Col>            
                  <h4 className={'heading-4'}>Program Description</h4>
                  <p className={'body-text'} style={{ whiteSpace: 'pre-wrap', }}>
                    {
                      (program.program_description != null && program.program_description !== undefined && program.program_description.length > 0)
                        ? program.program_description
                        : 'No description is available for this program.'
                    }
                  </p> 
                </Col>
              </Row>
              { 
                (program.is_virtual_learning === true) &&
                <Row className={'mb-4'}>
                  <Col>
                    <h4 className={'heading-4'}>Virtual Experience</h4>
                    <p className={'body-text'} style={{ whiteSpace: 'pre-wrap', }}>
                      This is an exclusively online or virtual experience.
                    </p>
                  </Col>
                </Row>
              }
              <Row className={'mb-4'}>
                <Col>
                  <h4 className={'heading-4'}>Occupation Description</h4>
                  <p className={'body-text'} style={{ whiteSpace: 'pre-wrap', }}>
                    {
                      (program.occupation_description != null && program.occupation_description !== undefined && program.occupation_description.length > 0)
                        ? program.occupation_description
                        : 'No description is available for this occupation.'
                    }
                  </p>
                </Col>
              </Row> 
              <Row>
                <ErrorBoundary>
                  <HowToApplySection
                    howToApplyContacts={program.apply_contacts}
                    programUrl={progWebsite}
                    programDocumentUrl={program.program_document}
                    applyInstructions={program.apply_instructions}
                    applyUrl={program.apply_url}
                    ref={howToApplyRef}
                    showHowToApply={showHowToApply}
                    setShowHowToApply={setShowHowToApply}
                  />
                </ErrorBoundary>
              </Row>
            </Col>
            { /* END PROGRAM INFORMATION COLUMN */}
  
            { /* BEGIN LOCATION AND MAP COLUMN */}
            <Col md={4} xs={12} className={'h-100'}>
              <ErrorBoundary>
                <DetailLocationSection
                  mapLibReady={mapLibReady}
                  program={program}
                  subscribeMapChanges={subscribeMapChanges}
                  programName={program.program_name}
                  programType={program.type}
                  userLocation={userLocation}
                  occupationName={program.occupation_name}
                />
              </ErrorBoundary>
            </Col>
          </Row>
          <Row className={'p-2 px-md-4 my-3'}>
            <Col>
              <ErrorBoundary>
                <ResourcesSection 
                  resources={program.equity_supports} 
                  pathname={pathname}
                  zipcode={zipcode}
                  ref={resourcesRef}
                />
              </ErrorBoundary>
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }
}


/**
 * Helper function to check if the input is null or undefined.
 * 
 * @param input 
 * @returns 
 */
function checkIfExists(input: any): boolean {
  if (input != null && input !== undefined) { return true }
  else { return false }
}

export default ProgramDetail